<script>
import { AbcBadge } from "@ordbogen-npm/abc-dsk";
import { mapStores, storeToRefs } from "pinia";
import { RouterLink } from "vue-router";

import { addPackageAndLangParams } from "@/common/route-path-helpers";
import WorkshopNav from "@/common/workshop-nav";
import NavbarItem from "@/components/header/NavbarItem.vue";
import WorkshopNavbar from "@/components/header/WorkshopNavbar.vue";
import IconSettings from "@/components/icons/green/IconGreenSettings.vue";
import IconWorkshop from "@/components/icons/green/IconGreenWorkshop.vue";
import IconHelp from "@/components/icons/IconHelp.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconLogout from "@/components/icons/IconLogout.vue";
import IconPen from "@/components/icons/IconPen.vue";
import IconVocab from "@/components/icons/IconVocab.vue";
import IconChevronDown from "@/components/icons/white/IconWhiteChevronDown.vue";
import IconSearch from "@/components/icons/white/IconWhiteSearch.vue";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useMenuStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default {
  name: "MobileMenu",
  components: {
    AbcBadge,
    IconChevronDown,
    IconHelp,
    IconInfo,
    IconLogout,
    IconPen,
    IconSearch,
    IconSettings,
    IconVocab,
    IconWorkshop,
    NavbarItem,
    WorkshopNavbar,
    RouterLink
  },
  data() {
    const authStore = useAuthStore();
    const { userIsTeacher } = storeToRefs(authStore);

    return {
      isWorkshopMenuOpen: false,
      userIsTeacher
    };
  },
  computed: {
    ...mapStores(useMenuStore),

    navigationItems() {
      return this.menuStore.menuItems;
    }
  },
  mounted() {
    window.addEventListener("keydown", this.escapeHandler);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.escapeHandler);
  },
  methods: {
    isWorkshopNavVisible: WorkshopNav.isVisible,

    hideMenu() {
      this.menuStore.hideMenu();
    },

    getWorkshopRoute() {
      return addPackageAndLangParams({ name: "workshop" });
    },

    toggleWorkshopMenu() {
      this.isWorkshopMenuOpen = !this.isWorkshopMenuOpen;
    },

    escapeHandler(ev) {
      if (ev.key !== "Escape") {
        return;
      }

      ev.stopPropagation();
      this.hideMenu();
    },

    trackExamInfoClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcExamPage,
        MatomoEventAction.ClickExamInfoButton,
        "Clicked topbar exam info"
      );
      this.hideMenu();
    },

    trackWorksheetsClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Worksheets,
        MatomoEventAction.ClickWorksheetsButton,
        "Clicked topbar worksheets"
      );
    }
  }
};
</script>

<template>
  <nav class="mobile-menu__menu">
    <ul class="list-unstyled m-0">
      <li class="mobile-menu__menu-item">
        <RouterLink class="mobile-menu__menu-item-link" :to="{ name: 'home' }" @click="hideMenu()">
          <div class="mobile-menu__menu-item-icon" aria-hidden="true">
            <IconSearch width="20" height="20" />
          </div>
          <div class="mobile-menu__menu-item-label">{{ $t("menu.dictionary") }}</div>
        </RouterLink>
      </li>
      <li v-if="isWorkshopNavVisible()" class="mobile-menu__menu-item">
        <RouterLink
          class="mobile-menu__menu-item-link"
          :to="getWorkshopRoute()"
          @click="hideMenu()"
        >
          <div class="mobile-menu__menu-item-icon" aria-hidden="true">
            <IconWorkshop width="20" height="20" />
          </div>
          <div class="mobile-menu__menu-item-label">{{ $t("menu.workshop") }}</div>
        </RouterLink>
        <button
          id="workshop-menu-mobile"
          class="mobile-menu__menu-item-chevron"
          :aria-label="$t('menu.submenuLabel')"
          :class="{ 'mobile-menu__menu-item-chevron--rotated': isWorkshopMenuOpen }"
          @click="toggleWorkshopMenu()"
        >
          <IconChevronDown role="presentation" />
        </button>
        <div v-if="isWorkshopMenuOpen" class="mobile-menu__submenu-container">
          <span class="mobile-menu__submenu-line"></span>
          <WorkshopNavbar
            id="workshop-navbar-mobile"
            block="navbar-mobile"
            trigger-id="workshop-menu-mobile"
          />
        </div>
      </li>
      <li class="mobile-menu__menu-item">
        <RouterLink
          class="mobile-menu__menu-item-link"
          :to="{ name: 'vocab.home' }"
          @click="hideMenu()"
        >
          <div class="mobile-menu__menu-item-icon" aria-hidden="true">
            <IconVocab width="20" height="20" />
          </div>
          <div class="mobile-menu__menu-item-label">{{ $t("menu.vocab") }}</div>
        </RouterLink>
      </li>
      <li v-if="userIsTeacher" class="mobile-menu__menu-item">
        <RouterLink
          class="mobile-menu__menu-item-link"
          :to="{ name: 'opgavehaefter' }"
          @click="trackWorksheetsClick()"
        >
          <div class="mobile-menu__menu-item-icon" aria-hidden="true">
            <IconPen width="20" height="20" />
          </div>
          <div class="mobile-menu__menu-item-label">{{ $t("menu.items.worksheets") }}</div>
          <AbcBadge class="header__badge" color="secondary">{{ $t("menu.badge") }}</AbcBadge>
        </RouterLink>
      </li>
      <li v-if="false" class="mobile-menu__menu-item">
        <RouterLink
          class="mobile-menu__menu-item-link"
          :to="{ name: 'exam-preparation' }"
          @click="trackExamInfoClick()"
        >
          <div class="mobile-menu__menu-item-icon" aria-hidden="true">
            <IconInfo width="20" height="20" />
          </div>
          <div class="mobile-menu__menu-item-label">{{ $t("menu.exam-info") }}</div>
        </RouterLink>
      </li>
      <NavbarItem
        v-for="(navItem, index) in navigationItems"
        :key="index"
        tag="li"
        class="mobile-menu__menu-item"
        :item="navItem"
        link-class="mobile-menu__menu-item-link"
        button-class="mobile-menu__menu-item-button"
        @hide="hideMenu()"
      >
        <template #icon-left="{ icon }">
          <div class="mobile-menu__menu-item-icon">
            <Component :is="icon" width="20" height="20" role="presentation" />
          </div>
        </template>
        <template #icon-right></template>
      </NavbarItem>
    </ul>
  </nav>
</template>
